import { createAdminSession, readAdminError } from './admin-session';
import { formatAcquisitionChannel, formatAddressLines, formatDateOnly, formatOrderMoney, getLocalDateInputValue } from './orders-admin-format';
import type { OrderShippingAddress } from './orders-admin-format';
import { ordersAdminStrings as strings } from './orders-admin-strings';

type OrderItem = {
    imageKey: string;
    title: string;
    printFileName: string | null;
    quantity: number;
    unitPriceCents: number;
    thumbnailUrl: string | null;
};

type Order = {
    id: number;
    orderNumber: string;
    status: string;
    customerEmail: string;
    customerName: string | null;
    shipping: OrderShippingAddress;
    subtotalCents: number;
    discountCents: number;
    taxCents: number;
    shippingCents: number;
    totalCents: number;
    currency: string;
    promotionCode: string | null;
    createdUtc: string;
    paidUtc: string | null;
    shipByDate: string | null;
    shipByState: 'overdue' | 'due-today' | 'upcoming' | null;
    printedUtc: string | null;
    shippedUtc: string | null;
    carrier: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    deliveredDate: string | null;
    returnDeadline: string | null;
    refundedUtc: string | null;
    refundedAfterReturnDeadline: boolean;
    confirmationEmailSentUtc: string | null;
    shippedEmailSentUtc: string | null;
    adminNotes: string | null;
    isReprint: boolean;
    replacesOrderNumber: string | null;
    reprintReason: string | null;
    reprintOrderNumbers: string[];
    stripePaymentUrl: string | null;
    items: OrderItem[];
    acquisitionChannel: string | null;
    acquisitionCampaignLabel: string | null;
    acquisitionReferrerHost: string | null;
};

type OrderPage = {
    orders: Order[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

type OrderActionResponse = {
    order: Order | null;
    warning: string | null;
};

const carriers = ['USPS', 'UPS', 'FedEx', 'Other'] as const;
const statusSurface = requiredElement<HTMLElement>('status');
const ordersSurface = requiredElement<HTMLElement>('orders');
const searchInput = requiredElement<HTMLInputElement>('order-search');
const viewSelect = requiredElement<HTMLSelectElement>('order-view');
const syncButton = requiredElement<HTMLButtonElement>('sync-button');
const previousPageButton = requiredElement<HTMLButtonElement>('previous-page-button');
const nextPageButton = requiredElement<HTMLButtonElement>('next-page-button');
const pageSummary = requiredElement<HTMLElement>('page-summary');
const modal = requiredElement<HTMLElement>('order-modal');
const modalHeading = requiredElement<HTMLElement>('order-modal-heading');
const modalSubheading = requiredElement<HTMLElement>('order-modal-subheading');
const modalStatus = requiredElement<HTMLElement>('order-modal-status');
const closeModalButton = requiredElement<HTMLButtonElement>('close-order-modal');
const detailSurface = requiredElement<HTMLElement>('order-detail');
let currentPage = 1;
let totalPages = 0;
let searchTimer: number | undefined;
let restoreFocusElement: HTMLElement | null = null;

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

function setModalStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    modalStatus.textContent = message;
    modalStatus.dataset.kind = kind;
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : strings.requestFailed;
}

const adminSession = createAdminSession({
    signedOutMessage: strings.signedOut,
    loginFailedMessage: strings.loginFailed,
    requestFailedMessage: strings.requestFailed,
    setStatus,
    onAuthenticated: loadOrders,
    onSignedOut: () => {
        closeModal(false);
        ordersSurface.replaceChildren();
        pageSummary.textContent = '';
    }
});

async function loadOrders(): Promise<void> {
    setStatus(strings.loading);
    const parameters = new URLSearchParams({
        search: searchInput.value.trim(),
        view: viewSelect.value,
        page: currentPage.toString(),
        pageSize: '25'
    });
    const response = await adminSession.request(`/api/admin/orders?${parameters}`);
    if (response.status === 404) {
        ordersSurface.replaceChildren();
        pageSummary.textContent = '';
        syncButton.disabled = true;
        setStatus(strings.shopDisabled);
        return;
    }
    if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
    const page = await response.json() as OrderPage;
    currentPage = page.page;
    totalPages = page.totalPages;
    ordersSurface.replaceChildren(...page.orders.map(createOrderRow));
    pageSummary.textContent = strings.pageSummary(page.page, page.totalPages, page.totalCount);
    previousPageButton.disabled = page.page <= 1;
    nextPageButton.disabled = page.totalPages === 0 || page.page >= page.totalPages;
    setStatus(page.orders.length === 0 ? strings.noOrders : '');
}

function createElement<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    if (className != null) element.className = className;
    if (text != null) element.textContent = text;
    return element;
}

function formatTimestamp(value: string): string {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? value : date.toLocaleString();
}

function createStatusBadge(order: Order): HTMLElement {
    // An unshipped reprint was never paid for, so it is labelled as a reprint rather than "Paid".
    const label = order.isReprint && order.status === 'paid' ? strings.reprintStatus : strings.status[order.status] ?? order.status;
    return createElement('span', `subscriber-badge order-status ${order.status}`, label);
}

function createShipByBadge(order: Order): HTMLElement | null {
    if (order.shipByState == null || order.shipByDate == null) return null;
    const text = order.shipByState === 'overdue'
        ? `${strings.overdue} · ${formatDateOnly(order.shipByDate)}`
        : order.shipByState === 'due-today'
            ? strings.dueToday
            : strings.shipBy(formatDateOnly(order.shipByDate));
    return createElement('span', `subscriber-badge ship-by ${order.shipByState}`, text);
}

function createThumbnail(item: OrderItem): HTMLElement {
    if (item.thumbnailUrl == null) return createElement('span', 'order-thumbnail missing', '?');
    const image = createElement('img', 'order-thumbnail');
    image.src = item.thumbnailUrl;
    image.alt = '';
    image.loading = 'lazy';
    return image;
}

function createOrderRow(order: Order): HTMLElement {
    const row = createElement('article', 'subscriber-row order-row');

    const identity = createElement('div', 'subscriber-identity');
    const heading = createElement('strong', undefined, order.isReprint ? `${order.orderNumber} (reprint)` : order.orderNumber);
    const customer = createElement('span', 'subscriber-dates', `${order.customerName ?? order.customerEmail} · ${formatTimestamp(order.paidUtc ?? order.createdUtc)}`);
    const items = createElement('div', 'order-row-items');
    for (const item of order.items) {
        const entry = createElement('span', 'order-row-item');
        entry.append(createThumbnail(item), document.createTextNode(item.quantity > 1 ? `${item.title} ×${item.quantity}` : item.title));
        items.append(entry);
    }
    identity.append(heading, customer, items);

    const badges = createElement('div', 'order-badges');
    badges.append(createStatusBadge(order));
    const shipBy = createShipByBadge(order);
    if (shipBy != null) badges.append(shipBy);
    badges.append(createElement('span', 'order-total', formatOrderMoney(order.totalCents, order.currency)));

    const actions = createElement('div', 'subscriber-actions');
    const openButton = createElement('button', 'secondary', 'Open');
    openButton.type = 'button';
    openButton.setAttribute('aria-label', strings.open(order.orderNumber));
    openButton.addEventListener('click', () => openOrder(order, openButton));
    actions.append(openButton);

    row.append(identity, badges, actions);
    return row;
}

async function copyText(text: string, button: HTMLButtonElement): Promise<void> {
    try {
        await navigator.clipboard.writeText(text);
        setModalStatus(strings.copied);
    } catch {
        setModalStatus(strings.copyFailed, 'error');
    }
    button.focus();
}

function createCopyButton(label: string, text: string): HTMLButtonElement {
    const button = createElement('button', 'secondary small-button', 'Copy');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.addEventListener('click', () => void copyText(text, button));
    return button;
}

function createSection(title: string): HTMLElement {
    const section = createElement('section', 'order-section');
    section.append(createElement('h3', undefined, title));
    return section;
}

function addTimelineEntry(list: HTMLDListElement, label: string, value: string | null, extra?: Node): void {
    if (value == null) return;
    const term = createElement('dt', undefined, label);
    const description = createElement('dd', undefined, value);
    if (extra != null) description.append(' ', extra);
    list.append(term, description);
}

function renderOrderDetail(order: Order): void {
    modalHeading.textContent = order.orderNumber;
    modalSubheading.replaceChildren(createStatusBadge(order));
    const shipBy = createShipByBadge(order);
    if (shipBy != null) modalSubheading.append(' ', shipBy);
    const parts: HTMLElement[] = [];

    if (order.isReprint && order.replacesOrderNumber != null) {
        parts.push(createElement('p', 'order-banner', strings.reprintOf(order.replacesOrderNumber, order.reprintReason)));
    } else if (order.reprintOrderNumbers.length > 0) {
        parts.push(createElement('p', 'order-banner', strings.reprintsCreated(order.reprintOrderNumbers)));
    }
    if (order.refundedAfterReturnDeadline) parts.push(createElement('p', 'order-banner warning', strings.refundedLate));

    const printList = createSection(strings.printList);
    const printItems = createElement('ul', 'order-print-list');
    for (const item of order.items) {
        const entry = createElement('li');
        const details = createElement('div');
        details.append(createElement('strong', undefined, item.title));
        const fileLine = createElement('div', 'order-file-line');
        if (item.printFileName != null) {
            fileLine.append(createElement('code', undefined, item.printFileName), createCopyButton(strings.copyFileName(item.printFileName), item.printFileName));
        } else {
            fileLine.append(createElement('span', 'subscriber-dates', strings.fileNameMissing));
        }
        details.append(fileLine);
        entry.append(createThumbnail(item), details, createElement('span', 'order-quantity', `×${item.quantity}`));
        printItems.append(entry);
    }
    printList.append(printItems);
    parts.push(printList);

    const shipTo = createSection(strings.shipTo);
    const addressLines = formatAddressLines(order.shipping);
    const address = createElement('address', 'order-address');
    addressLines.forEach((line, index) => {
        if (index > 0) address.append(createElement('br'));
        address.append(document.createTextNode(line));
    });
    const email = createElement('a', undefined, order.customerEmail);
    email.href = `mailto:${order.customerEmail}`;
    shipTo.append(address, createCopyButton(strings.copyAddress, addressLines.join('\n')), createElement('p', 'order-email'));
    shipTo.lastElementChild?.append(email);
    parts.push(shipTo);

    const totals = createSection(strings.totals);
    const totalsList = createElement('dl', 'order-timeline');
    addTimelineEntry(totalsList, 'Subtotal', formatOrderMoney(order.subtotalCents, order.currency));
    if (order.discountCents > 0) {
        addTimelineEntry(totalsList, 'Discount', `−${formatOrderMoney(order.discountCents, order.currency)}${order.promotionCode == null ? '' : ` (${order.promotionCode})`}`);
    }
    addTimelineEntry(totalsList, 'Shipping', order.shippingCents === 0 ? 'Free' : formatOrderMoney(order.shippingCents, order.currency));
    addTimelineEntry(totalsList, 'Tax', formatOrderMoney(order.taxCents, order.currency));
    addTimelineEntry(totalsList, 'Total', formatOrderMoney(order.totalCents, order.currency));
    totals.append(totalsList);
    parts.push(totals);

    const timeline = createSection(strings.timeline);
    const timelineList = createElement('dl', 'order-timeline');
    addTimelineEntry(timelineList, order.paidUtc == null ? strings.created : strings.paid, formatTimestamp(order.paidUtc ?? order.createdUtc));
    addTimelineEntry(timelineList, strings.cameFrom, formatAcquisitionChannel(order.acquisitionChannel, order.acquisitionCampaignLabel, order.acquisitionReferrerHost));
    addTimelineEntry(timelineList, strings.shipByLabel, order.shipByDate == null ? null : formatDateOnly(order.shipByDate));
    addTimelineEntry(timelineList, strings.printed, order.printedUtc == null ? null : formatTimestamp(order.printedUtc));
    let trackingLink: HTMLAnchorElement | undefined;
    if (order.trackingUrl != null) {
        trackingLink = createElement('a', undefined, 'Track');
        trackingLink.href = order.trackingUrl;
        trackingLink.target = '_blank';
        trackingLink.rel = 'noopener noreferrer';
    }
    addTimelineEntry(
        timelineList,
        strings.shipped,
        order.shippedUtc == null ? null : `${formatTimestamp(order.shippedUtc)} · ${order.carrier ?? ''} ${order.trackingNumber ?? ''}`.trim(),
        trackingLink);
    addTimelineEntry(timelineList, strings.delivered, order.deliveredDate == null ? null : formatDateOnly(order.deliveredDate));
    addTimelineEntry(timelineList, strings.returnDeadline, order.returnDeadline == null ? null : formatDateOnly(order.returnDeadline));
    addTimelineEntry(timelineList, strings.refunded, order.refundedUtc == null ? null : formatTimestamp(order.refundedUtc));
    addTimelineEntry(timelineList, order.isReprint ? strings.reprintEmail : strings.confirmationEmail, order.confirmationEmailSentUtc == null ? strings.notSent : formatTimestamp(order.confirmationEmailSentUtc));
    if (order.shippedUtc != null) {
        addTimelineEntry(timelineList, strings.shippedEmail, order.shippedEmailSentUtc == null ? strings.notSent : formatTimestamp(order.shippedEmailSentUtc));
    }
    timeline.append(timelineList);
    parts.push(timeline);

    const fulfilment = createSection('Fulfilment');
    const actionArea = createElement('div', 'order-actions');
    if (order.status === 'paid') {
        const printedButton = createElement('button', undefined, strings.markPrinted);
        printedButton.type = 'button';
        printedButton.addEventListener('click', () => void runOrderAction(order, printedButton, strings.markedPrinted, () =>
            postFulfilment(order.id, { action: 'printed' })));
        actionArea.append(printedButton);
    }
    if (order.status === 'paid' || order.status === 'printed' || order.status === 'shipped') {
        actionArea.append(createShippingForm(order));
    }
    if (order.status === 'shipped') {
        actionArea.append(createDeliveredForm(order), createReprintForm(order));
    }
    if (!order.isReprint) {
        const resendButton = createElement('button', 'secondary', strings.resendConfirmation);
        resendButton.type = 'button';
        resendButton.addEventListener('click', () => void runOrderAction(order, resendButton, strings.confirmationResent, () =>
            adminSession.request(`/api/admin/orders/${order.id}/resend-confirmation`, { method: 'POST' })));
        actionArea.append(resendButton);
    }
    fulfilment.append(actionArea);
    if (order.stripePaymentUrl != null) {
        const stripeLink = createElement('a', undefined, strings.stripePayment);
        stripeLink.href = order.stripePaymentUrl;
        stripeLink.target = '_blank';
        stripeLink.rel = 'noopener noreferrer';
        const hint = createElement('p', 'subscriber-dates');
        hint.append(stripeLink, document.createTextNode(` · ${strings.refundHint}`));
        fulfilment.append(hint);
    }
    parts.push(fulfilment);

    parts.push(createNotesForm(order));
    detailSurface.replaceChildren(...parts);
}

function createShippingForm(order: Order): HTMLFormElement {
    const form = createElement('form', 'order-inline-form');
    const carrierSelect = createElement('select');
    for (const carrier of carriers) {
        const option = createElement('option', undefined, carrier);
        option.value = carrier;
        carrierSelect.append(option);
    }
    carrierSelect.value = order.carrier ?? 'USPS';
    const carrierLabel = createElement('label', undefined, strings.carrier);
    carrierLabel.append(carrierSelect);
    const trackingInput = createElement('input');
    trackingInput.required = true;
    trackingInput.maxLength = 100;
    trackingInput.autocomplete = 'off';
    trackingInput.value = order.trackingNumber ?? '';
    const trackingLabel = createElement('label', undefined, strings.trackingNumber);
    trackingLabel.append(trackingInput);
    const updating = order.status === 'shipped';
    const submit = createElement('button', updating ? 'secondary' : undefined, updating ? strings.updateTracking : strings.markShipped);
    submit.type = 'submit';
    form.append(carrierLabel, trackingLabel, submit);
    form.addEventListener('submit', event => {
        event.preventDefault();
        void runOrderAction(order, submit, updating ? strings.trackingUpdated : strings.markedShipped, () =>
            postFulfilment(order.id, { action: 'shipped', carrier: carrierSelect.value, trackingNumber: trackingInput.value.trim() }));
    });
    return form;
}

function createDeliveredForm(order: Order): HTMLFormElement {
    const form = createElement('form', 'order-inline-form');
    const dateInput = createElement('input');
    dateInput.type = 'date';
    dateInput.required = true;
    dateInput.max = getLocalDateInputValue();
    dateInput.value = order.deliveredDate ?? getLocalDateInputValue();
    const label = createElement('label', undefined, strings.deliveredDate);
    label.append(dateInput);
    const submit = createElement('button', 'secondary', strings.markDelivered);
    submit.type = 'submit';
    form.append(label, submit);
    form.addEventListener('submit', event => {
        event.preventDefault();
        void runOrderAction(order, submit, strings.markedDelivered, () =>
            postFulfilment(order.id, { action: 'delivered', deliveredDate: dateInput.value }));
    });
    return form;
}

function createReprintForm(order: Order): HTMLFormElement {
    const form = createElement('form', 'order-inline-form order-reprint-form');
    form.append(createElement('h4', undefined, strings.reprintHeading));
    const reasonInput = createElement('input');
    reasonInput.required = true;
    reasonInput.maxLength = 500;
    reasonInput.placeholder = strings.reprintPlaceholder;
    const label = createElement('label', undefined, strings.reprintReason);
    label.append(reasonInput);
    const submit = createElement('button', 'danger', strings.sendReprint);
    submit.type = 'submit';
    form.append(label, submit);
    form.addEventListener('submit', event => {
        event.preventDefault();
        if (!window.confirm(strings.confirmReprint(order.orderNumber))) return;
        void runOrderAction(order, submit, '', () => adminSession.request(`/api/admin/orders/${order.id}/reprint`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reason: reasonInput.value.trim() })
        }), reprint => strings.reprintCreated(reprint.orderNumber));
    });
    return form;
}

function createNotesForm(order: Order): HTMLElement {
    const section = createSection(strings.notes);
    const form = createElement('form', 'order-notes-form');
    const textarea = createElement('textarea');
    textarea.rows = 3;
    textarea.maxLength = 4000;
    textarea.value = order.adminNotes ?? '';
    textarea.setAttribute('aria-label', strings.notes);
    const submit = createElement('button', 'secondary', strings.saveNotes);
    submit.type = 'submit';
    form.append(textarea, submit);
    form.addEventListener('submit', event => {
        event.preventDefault();
        void runOrderAction(order, submit, strings.notesSaved, () => adminSession.request(`/api/admin/orders/${order.id}/notes`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ notes: textarea.value })
        }));
    });
    section.append(form);
    return section;
}

function postFulfilment(orderId: number, body: Record<string, string>): Promise<Response> {
    return adminSession.request(`/api/admin/orders/${orderId}/fulfilment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });
}

/**
 * Runs one order mutation, then shows the returned order (a reprint opens the new order) and refreshes the list.
 * A server warning, such as an email that could not be sent, replaces the success message.
 */
async function runOrderAction(
    order: Order,
    button: HTMLButtonElement,
    successMessage: string,
    action: () => Promise<Response>,
    describeResult?: (result: Order) => string
): Promise<void> {
    button.disabled = true;
    setModalStatus('');
    try {
        const response = await action();
        if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
        const payload = await response.json() as OrderActionResponse;
        const updated = payload.order ?? order;
        renderOrderDetail(updated);
        const message = describeResult?.(updated) ?? successMessage;
        setModalStatus(payload.warning ?? message, payload.warning == null ? 'status' : 'error');
        await loadOrders();
    } catch (error) {
        setModalStatus(errorMessage(error), 'error');
    } finally {
        button.disabled = false;
    }
}

function openOrder(order: Order, trigger: HTMLElement): void {
    restoreFocusElement = trigger;
    setModalStatus('');
    renderOrderDetail(order);
    modal.hidden = false;
    document.body.classList.add('modal-open');
    closeModalButton.focus();
}

function closeModal(restoreFocus = true): void {
    if (modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('modal-open');
    detailSurface.replaceChildren();
    if (restoreFocus) restoreFocusElement?.focus();
    restoreFocusElement = null;
}

async function syncFromStripe(): Promise<void> {
    syncButton.disabled = true;
    setStatus(strings.syncing);
    try {
        const response = await adminSession.request('/api/admin/orders/sync', { method: 'POST' });
        if (!response.ok) throw new Error(await readAdminError(response, strings.requestFailed));
        const result = await response.json() as { checked: number; imported: number };
        await loadOrders();
        setStatus(strings.syncResult(result.checked, result.imported));
    } catch (error) {
        setStatus(errorMessage(error), 'error');
    } finally {
        syncButton.disabled = false;
    }
}

function reload(): void {
    void loadOrders().catch(error => setStatus(errorMessage(error), 'error'));
}

closeModalButton.addEventListener('click', () => closeModal());
modal.addEventListener('click', event => {
    if (event.target === modal) closeModal();
});
modal.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(modal.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'))
        .filter(element => !element.closest('[hidden]'));
    const first = focusable[0];
    const last = focusable.at(-1);
    if (first == null || last == null) return;
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
});
syncButton.addEventListener('click', () => void syncFromStripe());
searchInput.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
        currentPage = 1;
        reload();
    }, 300);
});
viewSelect.addEventListener('change', () => {
    currentPage = 1;
    reload();
});
previousPageButton.addEventListener('click', () => {
    if (currentPage <= 1) return;
    currentPage--;
    reload();
});
nextPageButton.addEventListener('click', () => {
    if (currentPage >= totalPages) return;
    currentPage++;
    reload();
});

void adminSession.initialize().catch(error => setStatus(errorMessage(error), 'error'));
